import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { installTelemetry } from "./services/telemetry";
import ErrorBoundary from "./components/ErrorBoundary";

// Install telemetry once, before any render
installTelemetry();

// Listen for session-expired events fired by axios 401 interceptor
if (typeof window !== 'undefined') {
  window.addEventListener('session-expired', () => {
    toast.error('Session expirée — Veuillez vous reconnecter', { duration: 5000 });
  });
}

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import ClientsPage from "./pages/ClientsPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import DevisListPage from "./pages/DevisListPage";
import DevisEditPage from "./pages/DevisEditPage";
import NewDevisPage from "./pages/NewDevisPage";
import SortiePage from "./pages/SortiePage";
import FournisseursPage from "./pages/FournisseursPage";
import FraisPage from "./pages/FraisPage";
import ProjetsPage from "./pages/ProjetsPage";
import ProjetDetailPage from "./pages/ProjetDetailPage";
import NewProjetPage from "./pages/NewProjetPage";
import BaseMaagPage from "./pages/BaseMaagPage";
import DevisTypesPage from "./pages/DevisTypesPage";
import PlanningPage from "./pages/PlanningPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import DiagnosticsPage from "./pages/DiagnosticsPage";
import SuiviBancairePage from "./pages/SuiviBancairePage";
import ArchivesPage from "./pages/ArchivesPage";
import PublicSignaturePage from "./pages/PublicSignaturePage";
import ContactsPage from "./pages/ContactsPage";
import FeedbackAdminPage from "./pages/FeedbackAdminPage";

// Components
import Layout from "./components/Layout";
import ChangePasswordModal from "./components/ChangePasswordModal";
import FeedbackButton from "./components/FeedbackButton";

// Session Warning Component — modale centrale prominente
const SessionWarning = () => {
  const { sessionWarning, extendSession, logout } = useAuth();
  const [countdown, setCountdown] = React.useState(300); // 5 min = 300s

  React.useEffect(() => {
    if (!sessionWarning) {
      setCountdown(300);
      return;
    }
    setCountdown(300);
    const interval = setInterval(() => {
      setCountdown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [sessionWarning]);

  if (!sessionWarning) return null;

  const mins = Math.floor(countdown / 60);
  const secs = countdown % 60;

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4"
      data-testid="session-warning-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border-2 border-amber-400">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 text-2xl">
            ⏰
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-slate-900">Êtes-vous toujours là ?</h3>
            <p className="text-sm text-slate-600 mt-1">
              Votre session va expirer par sécurité. Fermeture automatique dans :
            </p>
          </div>
        </div>
        <div className="text-center my-6">
          <div className="text-5xl font-bold text-amber-600 tabular-nums">
            {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Cliquez "Oui, continuer" pour prolonger d'1 heure
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={extendSession}
            className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 active:bg-emerald-800 transition"
            data-testid="session-extend-btn"
          >
            ✓ Oui, continuer (1h)
          </button>
          <button
            onClick={logout}
            className="flex-1 px-4 py-3 border-2 border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition"
            data-testid="session-logout-btn"
          >
            Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );
};

// Protected Route wrapper with password change check
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading, mustChangePassword, isAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Show password change modal if required
  if (mustChangePassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <ChangePasswordModal forced={true} />
      </div>
    );
  }
  
  // Check admin requirement
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }
  
  return (
    <Layout>
      {children}
      <SessionWarning />
      <FeedbackButton />
    </Layout>
  );
};

// Public Route wrapper (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public signature page (no auth required) */}
      <Route path="/signature/:token" element={<PublicSignaturePage />} />
      
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <ClientsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts"
        element={
          <ProtectedRoute>
            <ContactsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/:clientId"
        element={
          <ProtectedRoute>
            <ClientDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/devis"
        element={
          <ProtectedRoute>
            <DevisListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/devis/nouveau"
        element={
          <ProtectedRoute>
            <NewDevisPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/devis/:devisId"
        element={
          <ProtectedRoute>
            <DevisEditPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/devis/:devisId/sortie"
        element={
          <ProtectedRoute>
            <SortiePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fournisseurs"
        element={
          <ProtectedRoute>
            <FournisseursPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/frais"
        element={
          <ProtectedRoute>
            <FraisPage />
          </ProtectedRoute>
        }
      />
      
      {/* Projets routes */}
      <Route
        path="/projets"
        element={
          <ProtectedRoute>
            <ProjetsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projets/nouveau"
        element={
          <ProtectedRoute>
            <NewProjetPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projets/:projetId"
        element={
          <ProtectedRoute>
            <ProjetDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projets/:projetId/marge"
        element={
          <ProtectedRoute>
            <SortiePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projets/:projetId/devis/nouveau"
        element={
          <ProtectedRoute>
            <NewDevisPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/planning/:planningId"
        element={
          <ProtectedRoute>
            <PlanningPage />
          </ProtectedRoute>
        }
      />

      {/* Base MAAG */}
      <Route
        path="/base-maag"
        element={
          <ProtectedRoute>
            <BaseMaagPage />
          </ProtectedRoute>
        }
      />

      {/* Devis Types par Enseigne */}
      <Route
        path="/enseignes/:enseigneId/devis-types"
        element={
          <ProtectedRoute>
            <DevisTypesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/archives"
        element={
          <ProtectedRoute>
            <ArchivesPage />
          </ProtectedRoute>
        }
      />

      {/* Admin routes */}
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requireAdmin={true}>
            <AdminUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/diagnostics"
        element={
          <ProtectedRoute requireAdmin={true}>
            <DiagnosticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/suivi-bancaire"
        element={
          <ProtectedRoute requireAdmin={true}>
            <SuiviBancairePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/modifications"
        element={
          <ProtectedRoute>
            <FeedbackAdminPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            style: {
              fontFamily: 'Inter, sans-serif'
            }
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
